import { useEffect, useRef, useState } from "react";
import { Eraser } from "lucide-react";

/** Lightweight canvas-based signature pad — mouse + touch, no external library. */
export const SignaturePad = ({ onChange }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPoint = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#1B2B22";
  }, []);

  const getPoint = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    lastPoint.current = getPoint(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const point = getPoint(e);
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPoint.current = point;
    if (isEmpty) setIsEmpty(false);
  };

  const end = () => {
    drawing.current = false;
    if (!isEmpty) onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
    onChange?.(null);
  };

  return (
    <div data-testid="signature-pad">
      <div className="relative rounded-xl border-2 border-dashed border-[#B8860B]/40 bg-white h-40">
        {isEmpty && (
          <span className="absolute inset-0 flex items-center justify-center text-sm text-gray-400 pointer-events-none">
            Sign here with your mouse, finger, or stylus
          </span>
        )}
        <canvas
          ref={canvasRef}
          data-testid="signature-pad-canvas"
          className="w-full h-full touch-none cursor-crosshair"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
      </div>
      <button
        type="button"
        onClick={clear}
        data-testid="signature-pad-clear"
        className="mt-2 flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700"
      >
        <Eraser size={13} /> Clear signature
      </button>
    </div>
  );
};
